// SeoResultDashboard.jsx
import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import jsPDF from 'jspdf';
import autoTable from "jspdf-autotable";
import './SeoResultDashboard.css';
import Navbar from '../Common/Navbar';
import MainFooter from '../Common/MainFooter';

const SeoResultDashboard = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const API_BASE_URL = "https://elexyt2024.site/api/seo/audit";

  // Helper function to get client-friendly error messages
  const getClientFriendlyError = (err) => {
    // Network/Connection errors
    if (err.message === 'Failed to fetch' || err.message.includes('NetworkError')) {
      return {
        title: 'Connection Issue',
        message: 'We\'re having trouble connecting to our analysis service. Please check your internet connection and try again.',
        icon: '🌐'
      };
    }

    // Timeout errors
    if (err.message.includes('timeout')) {
      return {
        title: 'Request Timeout',
        message: 'The analysis is taking longer than expected. The website might be slow to respond. Please try again.',
        icon: '⏱️'
      };
    }

    // HTTP Status errors
    if (err.message.includes('status 404')) {
      return {
        title: 'Service Not Available',
        message: 'Our SEO analysis service is temporarily unavailable. Please try again in a few moments.',
        icon: '🔧'
      };
    }

    if (err.message.includes('status 500') || err.message.includes('status 503')) {
      return {
        title: 'Service Error',
        message: 'We encountered an error while analyzing your website. Our team has been notified. Please try again later.',
        icon: '⚠️'
      };
    }

    if (err.message.includes('status 429')) {
      return {
        title: 'Too Many Requests',
        message: 'You\'ve reached the analysis limit. Please wait a few minutes before trying again.',
        icon: '⏳'
      };
    }

    // Invalid URL or website errors
    if (err.message.includes('invalid URL') || err.message.includes('malformed')) {
      return {
        title: 'Invalid Website URL',
        message: 'The website URL appears to be invalid. Please check the URL and try again.',
        icon: '🔗'
      };
    }

    // Website unreachable
    if (err.message.includes('unreachable') || err.message.includes('not accessible')) {
      return {
        title: 'Website Unreachable',
        message: 'We couldn\'t access the website you provided. Please verify the URL is correct and the website is online.',
        icon: '🚫'
      };
    }

    // CORS or HTML response errors (backend issue but shown nicely)
    if (err.message.includes('HTML instead of JSON') || err.message.includes('CORS')) {
      return {
        title: 'Analysis Unavailable',
        message: 'We\'re experiencing technical difficulties with our analysis service. Please try again shortly.',
        icon: '🔧'
      };
    }

    // Default generic error
    return {
      title: 'Analysis Failed',
      message: 'We encountered an unexpected error while analyzing your website. Please try again or contact support if the problem persists.',
      icon: '❌'
    };
  };

  useEffect(() => {
    const url = searchParams.get('url');
    
    if (!url) {
      setError({
        title: 'No Website Provided',
        message: 'Please enter a website URL to analyze.',
        icon: '🔍'
      });
      setLoading(false);
      return;
    }

    // Fetch SEO analysis data
    fetchSeoData(url);
  }, [searchParams]);

  const fetchSeoData = async (url) => {
    try {
      setLoading(true);
      setError(null);

      const response = await fetch(`${API_BASE_URL}?url=${url}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`status ${response.status}`);
      }

      const contentType = response.headers.get("content-type");
      if (!contentType || !contentType.includes("application/json")) {
        const textResponse = await response.text();
        throw new Error('HTML instead of JSON');
      }

      const result = await response.json();
      
      setData(result);
      setLoading(false);

    } catch (err) {
      const friendlyError = getClientFriendlyError(err);
      setError(friendlyError);
      setLoading(false);
    }
  };

  // Calculate SEO score based on issues severity
  const calculateScore = () => {
    if (!data || !data.issues || data.issues.length === 0) return 100;
    
    const high = data.issues.filter(i => i.severity === 'HIGH').length;
    const medium = data.issues.filter(i => i.severity === 'MEDIUM').length;
    const low = data.issues.filter(i => i.severity === 'LOW').length;
    
    const score = Math.max(0, 100 - (high * 20) - (medium * 10) - (low * 3));
    return Math.round(score);
  };

  // Group issues by severity
  const getGroupedIssues = () => {
    if (!data || !data.issues) return {};
    
    return data.issues.reduce((acc, issue) => {
      if (!acc[issue.severity]) acc[issue.severity] = [];
      acc[issue.severity].push(issue);
      return acc;
    }, {});
  };

const generatePDFReport = () => {

  if (!data) return;

  const doc = new jsPDF();

  // ⭐ IMPORTANT FIX — replace ALL doc.autoTable() usages with autoTable(doc, {...})

  // Calculate score for PDF
  const score = data.score || calculateScore();

  // Group issues for PDF
  const groupedIssues = getGroupedIssues();

  // Colors
  const primaryColor = [13, 110, 253];
  const darkGray = [51, 51, 51];
  const mediumGray = [102, 102, 102];
  const lightGray = [245, 245, 245];
  const redColor = [220, 53, 69];
  const orangeColor = [255, 193, 7];
  const greenColor = [25, 135, 84];

  let yPosition = 20;

  // ============ HEADER SECTION ============
  doc.setFillColor(...primaryColor);
  doc.rect(0, 0, 210, 35, "F");

  doc.setTextColor(255, 255, 255);
  doc.setFontSize(24);
  doc.setFont("helvetica", "bold");
  doc.text("SEO AUDIT REPORT", 105, 15, { align: "center" });

  doc.setFontSize(10);
  doc.setFont("helvetica", "normal");
  doc.text("Comprehensive Website Analysis", 105, 23, { align: "center" });

  yPosition = 45;

  // ============ WEBSITE INFO ============
  doc.setTextColor(...darkGray);
  doc.setFontSize(10);
  doc.setFont("helvetica", "bold");
  doc.text("Website URL:", 20, yPosition);

  doc.setFont("helvetica", "normal");
  doc.setTextColor(...primaryColor);
  doc.text(data.url || "N/A", 50, yPosition);

  yPosition += 7;
  doc.setTextColor(...mediumGray);
  doc.setFontSize(9);

  const generatedDate = data.generatedAt
    ? new Date(data.generatedAt).toLocaleString()
    : new Date().toLocaleString();

  doc.text(`Report Generated: ${generatedDate}`, 20, yPosition);

  yPosition += 10;

  // ============ SCORE BOX ============
  doc.setFillColor(...lightGray);
  doc.roundedRect(20, yPosition, 170, 25, 3, 3, "F");

  const scoreColor =
    score >= 80 ? greenColor :
    score >= 60 ? primaryColor :
    score >= 40 ? orangeColor :
    redColor;

  doc.setFillColor(...scoreColor);
  doc.circle(40, yPosition + 12.5, 10, "F");

  doc.setTextColor(255, 255, 255);
  doc.setFontSize(16);
  doc.setFont("helvetica", "bold");
  doc.text(score.toString(), 40, yPosition + 14, { align: "center" });

  doc.setTextColor(...darkGray);
  doc.setFontSize(14);
  doc.text("Overall SEO Score", 60, yPosition + 10);

  doc.setFontSize(10);
  const scoreStatus =
    score >= 80 ? "Excellent" :
    score >= 60 ? "Good" :
    score >= 40 ? "Needs Improvement" :
    "Poor";

  doc.setTextColor(...scoreColor);
  doc.text(`Status: ${scoreStatus}`, 60, yPosition + 18);

  yPosition += 35;

  // ============ QUICK STATS ============
  doc.setFontSize(12);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(...darkGray);
  doc.text("Quick Statistics", 20, yPosition);

  yPosition += 8;

  const stats = [
    ["Load Time", `${data.loadTimeMs || 0}ms`, (data.loadTimeMs || 0) < 3000 ? "Good" : "Needs Improvement"],
    ["Security", data.usesHttps ? "HTTPS Enabled" : "HTTP Only", data.usesHttps ? "Secure" : "Warning"],
    ["HTTP Status", (data.httpStatus || "N/A").toString(), data.httpStatus === 200 ? "OK" : "Check Required"],
    ["Total Links", (data.totalLinks || 0).toString(), "Info"],
    ["Internal Links", (data.internalLinks || 0).toString(), "Info"],
    ["External Links", (data.externalLinks || 0).toString(), "Info"],
  ];

  // ⭐ FIXED: Replace doc.autoTable → autoTable(doc, {...})
  autoTable(doc, {
    startY: yPosition,
    head: [["Metric", "Value", "Status"]],
    body: stats,
    theme: "striped",
    headStyles: {
      fillColor: primaryColor,
      fontSize: 10,
      fontStyle: "bold",
      halign: "left",
    },
    styles: {
      fontSize: 9,
      cellPadding: 4,
    },
    columnStyles: {
      0: { fontStyle: "bold", cellWidth: 50 },
      1: { cellWidth: 70 },
      2: { cellWidth: 50, halign: "center" },
    },
  });

  yPosition = doc.lastAutoTable.finalY + 15;

  // ========= META INFO =========

  doc.setFontSize(12);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(...darkGray);
  doc.text("Meta Information", 20, yPosition);

  yPosition += 8;

  const metaData = [
    ["Page Title", data.title || "Not set", `${data.titleLength || 0} chars`],
    ["Meta Description", data.metaDescription || "Missing", `${data.metaDescriptionLength || 0} chars`],
    ["H1 Tags", (data.h1Tags ? data.h1Tags.length : 0).toString(), data.h1Tags?.length === 1 ? "Optimal" : "Check"],
    ["H2 Tags", (data.h2Tags ? data.h2Tags.length : 0).toString(), "Info"],
  ];

  autoTable(doc, {
    startY: yPosition,
    head: [["Element", "Content", "Status"]],
    body: metaData,
    theme: "striped",
    headStyles: {
      fillColor: primaryColor,
      fontSize: 10,
      fontStyle: "bold",
    },
    styles: {
      fontSize: 9,
      cellPadding: 4,
    },
    columnStyles: {
      0: { fontStyle: "bold", cellWidth: 50 },
      1: { cellWidth: 90 },
      2: { cellWidth: 30, halign: "center" },
    },
  });

  yPosition = doc.lastAutoTable.finalY + 15;

  // ========= TECHNICAL DETAILS =========

  if (yPosition > 250) {
    doc.addPage();
    yPosition = 20;
  }

  doc.setFontSize(12);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(...darkGray);
  doc.text("Technical Details", 20, yPosition);

  yPosition += 8;

  const technicalData = [
    ["Canonical Tag", data.hasCanonical ? "Yes" : "No", data.hasCanonical ? "✓" : "✗"],
    ["Structured Data", data.hasJsonLd ? "Yes" : "No", data.hasJsonLd ? "✓" : "✗"],
    ["Sitemap Found", data.sitemapFound ? "Yes" : "No", data.sitemapFound ? "✓" : "✗"],
    ["Robots.txt", data.robotsTxtAccessible ? "Accessible" : "Not Found", data.robotsTxtAccessible ? "✓" : "✗"],
    ["Images Missing Alt", (data.imagesMissingAlt || 0).toString(), data.imagesMissingAlt === 0 ? "✓" : "✗"],
  ];

  autoTable(doc, {
    startY: yPosition,
    head: [["Feature", "Status", "Check"]],
    body: technicalData,
    theme: "striped",
    headStyles: {
      fillColor: primaryColor,
      fontSize: 10,
      fontStyle: "bold",
    },
    styles: {
      fontSize: 9,
      cellPadding: 4,
    },
  });

  yPosition = doc.lastAutoTable.finalY + 15;

  // ============ ISSUES SECTION ============

  if (data.issues && data.issues.length > 0) {

    if (yPosition > 230) {
      doc.addPage();
      yPosition = 20;
    }

    doc.setFontSize(12);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(...darkGray);
    doc.text(`Issues Detected (${data.issues.length})`, 20, yPosition);

    yPosition += 8;

    // HIGH PRIORITY
    if (groupedIssues.HIGH?.length) {
      doc.setFontSize(11);
      doc.setTextColor(...redColor);
      doc.text(`High Priority Issues (${groupedIssues.HIGH.length})`, 20, yPosition);

      yPosition += 6;

      const highIssuesData = groupedIssues.HIGH.map((issue) => [
        issue.ruleCode.replace(/_/g, " "),
        issue.message,
        "HIGH",
      ]);

      autoTable(doc, {
        startY: yPosition,
        head: [["Issue", "Description", "Severity"]],
        body: highIssuesData,
        theme: "grid",
        headStyles: {
          fillColor: redColor,
          fontSize: 9,
          fontStyle: "bold",
        },
        styles: {
          fontSize: 8,
          cellPadding: 3,
        },
      });

      yPosition = doc.lastAutoTable.finalY + 10;
    }

    // MEDIUM PRIORITY
    if (groupedIssues.MEDIUM?.length) {

      if (yPosition > 240) {
        doc.addPage();
        yPosition = 20;
      }

      doc.setFontSize(11);
      doc.setTextColor(...orangeColor);
      doc.text(`Medium Priority Issues (${groupedIssues.MEDIUM.length})`, 20, yPosition);

      yPosition += 6;

      const mediumIssuesData = groupedIssues.MEDIUM.map((issue) => [
        issue.ruleCode.replace(/_/g, " "),
        issue.message,
        "MEDIUM",
      ]);

      autoTable(doc, {
        startY: yPosition,
        head: [["Issue", "Description", "Severity"]],
        body: mediumIssuesData,
        theme: "grid",
        headStyles: {
          fillColor: orangeColor,
          fontSize: 9,
          fontStyle: "bold",
        },
        styles: {
          fontSize: 8,
          cellPadding: 3,
        },
      });

      yPosition = doc.lastAutoTable.finalY + 10;
    }

    // LOW PRIORITY
    if (groupedIssues.LOW?.length) {

      if (yPosition > 240) {
        doc.addPage();
        yPosition = 20;
      }

      doc.setFontSize(11);
      doc.setTextColor(...primaryColor);
      doc.text(`Low Priority Issues (${groupedIssues.LOW.length})`, 20, yPosition);

      yPosition += 6;

      const lowIssuesData = groupedIssues.LOW.map((issue) => [
        issue.ruleCode.replace(/_/g, " "),
        issue.message,
        "LOW",
      ]);

      autoTable(doc, {
        startY: yPosition,
        head: [["Issue", "Description", "Severity"]],
        body: lowIssuesData,
        theme: "grid",
        headStyles: {
          fillColor: primaryColor,
          fontSize: 9,
          fontStyle: "bold",
        },
        styles: {
          fontSize: 8,
          cellPadding: 3,
        },
      });

      yPosition = doc.lastAutoTable.finalY + 10;
    }
  }

  // ============ RECOMMENDATIONS ============

  if (data.recommendations && Object.keys(data.recommendations).length > 0) {

    if (yPosition > 200) {
      doc.addPage();
      yPosition = 20;
    }

    doc.setFontSize(12);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(...darkGray);
    doc.text("Priority Recommendations", 20, yPosition);

    yPosition += 8;

    const recommendationsData = Object.entries(data.recommendations).map(([key, value]) => [
      key.replace(/_/g, " "),
      value,
    ]);

    autoTable(doc, {
      startY: yPosition,
      head: [["Issue", "Recommended Fix"]],
      body: recommendationsData,
      theme: "striped",
      headStyles: {
        fillColor: greenColor,
        fontSize: 10,
        fontStyle: "bold",
      },
      styles: {
        fontSize: 8,
        cellPadding: 4,
      },
      columnStyles: {
        0: { cellWidth: 60, fontStyle: "bold" },
        1: { cellWidth: 110 },
      },
    });
  }

  // ============ FOOTER ============

  const pageCount = doc.internal.getNumberOfPages();

  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.setFontSize(8);
    doc.setTextColor(...mediumGray);
    doc.text(
      `SEO Audit Report - ${data.url || "N/A"} - Page ${i} of ${pageCount}`,
      105,
      290,
      { align: "center" }
    );
  }

  doc.save(`seo-report-${new Date().getTime()}.pdf`);
};

  // Loading state
  if (loading) {
    return (
      <>
        <Navbar />
        <div className="loading-container">
          <div className="loading-content">
            <div className="spinner-wrapper">
              <div className="spinner-outer"></div>
              <div className="spinner-inner"></div>
              <div className="spinner-center">
                <svg width="40" height="40" viewBox="0 0 40 40" fill="none">
                  <path d="M20 5L25 15H15L20 5Z" fill="#0d6efd"/>
                  <path d="M35 20L25 15L30 25L35 20Z" fill="#0d6efd"/>
                  <path d="M20 35L15 25H25L20 35Z" fill="#0d6efd"/>
                  <path d="M5 20L15 25L10 15L5 20Z" fill="#0d6efd"/>
                </svg>
              </div>
            </div>
            <h3 className="loading-title">Analyzing Your Website</h3>
            <p className="loading-subtitle">Scanning SEO metrics and performance...</p>
            <div className="loading-steps">
              <div className="loading-step active">
                <div className="step-icon">🔍</div>
                <span>Crawling pages</span>
              </div>
              <div className="loading-step active delay-1">
                <div className="step-icon">📊</div>
                <span>Analyzing data</span>
              </div>
              <div className="loading-step active delay-2">
                <div className="step-icon">⚡</div>
                <span>Generating report</span>
              </div>
            </div>
          </div>
        </div>
        
        <MainFooter />
      </>
    );
  }

  // Error state - CLIENT FRIENDLY VERSION
  if (error) {
    return (
      <>
        <Navbar />
        <div style={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          minHeight: '80vh',
          flexDirection: 'column',
          gap: '20px',
          padding: '20px'
        }}>
          <div className="error-container" style={{
            maxWidth: '500px',
            textAlign: 'center',
            padding: '40px',
            background: '#fff',
            borderRadius: '16px',
            boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
          }}>
            <div style={{ fontSize: '64px', marginBottom: '20px' }}>
              {error.icon}
            </div>
            <h2 style={{ 
              color: '#333', 
              marginBottom: '16px',
              fontSize: '24px',
              fontWeight: '600'
            }}>
              {error.title}
            </h2>
            <p style={{ 
              color: '#666', 
              fontSize: '16px', 
              lineHeight: '1.6',
              marginBottom: '30px'
            }}>
              {error.message}
            </p>
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
              <button 
                className="btn btn-primary"
                onClick={() => {
                  const url = searchParams.get('url');
                  if (url) fetchSeoData(url);
                }}
                style={{
                  padding: '12px 24px',
                  fontSize: '16px',
                  borderRadius: '8px'
                }}
              >
                🔄 Try Again
              </button>
              <button 
                className="btn btn-outline-secondary"
                onClick={() => navigate('/')}
                style={{
                  padding: '12px 24px',
                  fontSize: '16px',
                  borderRadius: '8px'
                }}
              >
                ← Go Back
              </button>
            </div>
            <p style={{ 
              marginTop: '24px', 
              fontSize: '14px', 
              color: '#999' 
            }}>
              Need help? <a href="/contact" style={{ color: '#0d6efd' }}>Contact Support</a>
            </p>
          </div>
        </div>
        <MainFooter />
      </>
    );
  }

  // No data state
  if (!data) {
    return null;
  }

  // Main dashboard rendering starts here
  const score = data.score || calculateScore();

  // Get score status class for color coding
  const getScoreClass = () => {
    if (score >= 80) return 'score-excellent';
    if (score >= 60) return 'score-good';
    if (score >= 40) return 'score-average';
    return 'score-poor';
  };

  // Get severity badge class
  const getSeverityClass = (severity) => {
    switch (severity) {
      case 'HIGH': return 'severity-high';
      case 'MEDIUM': return 'severity-medium';
      case 'LOW': return 'severity-low';
      case 'INFO': return 'severity-info';
      default: return 'severity-info';
    }
  };

  // Group issues by severity for organized display
  const groupedIssues = getGroupedIssues();

  return (
    <>
      <Navbar />
      
      <div className="seo-dashboard">
        {/* Header Section with Score */}
        <div className="dashboard-header">
          <div className="header-info">
            <h2 className="dashboard-title">SEO Audit Results</h2>
            <p className="analyzed-url">{data.url || 'N/A'}</p>
            <p className="analysis-date">
              Analyzed: {data.generatedAt ? new Date(data.generatedAt).toLocaleString() : new Date().toLocaleString()}
            </p>
          </div>
          <div className="score-container">
            <div className={`score-circle ${getScoreClass()}`}>
              <span className="score-number">{score}</span>
              <span className="score-label">Score</span>
            </div>
          </div>
        </div>

        {/* Download Report Button */}
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          margin: '20px 0',
          padding: '0 20px'
        }}>
          <button 
            className="btn btn-success btn-lg"
            onClick={generatePDFReport}
            style={{
              padding: '14px 32px',
              fontSize: '16px',
              fontWeight: '600',
              borderRadius: '8px',
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
              transition: 'all 0.3s ease',
              border: 'none',
              backgroundColor: '#198754',
              color: 'white',
              cursor: 'pointer'
            }}
            onMouseEnter={(e) => {
              e.target.style.transform = 'translateY(-2px)';
              e.target.style.boxShadow = '0 6px 12px rgba(0,0,0,0.15)';
            }}
            onMouseLeave={(e) => {
              e.target.style.transform = 'translateY(0)';
              e.target.style.boxShadow = '0 4px 6px rgba(0,0,0,0.1)';
            }}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
              <polyline points="7 10 12 15 17 10"></polyline>
              <line x1="12" y1="15" x2="12" y2="3"></line>
            </svg>
            Download PDF Report
          </button>
        </div>

         {/* Quick Stats Grid */}
        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-icon">⚡</div>
            <div className="stat-content">
              <span className="stat-label">Load Time</span>
              <span className="stat-value">{data.loadTimeMs}ms</span>
            </div>
          </div>

          <div className="stat-card">
            <div className="stat-icon">{data.usesHttps ? '🔒' : '⚠️'}</div>
            <div className="stat-content">
              <span className="stat-label">Security</span>
              <span className="stat-value">{data.usesHttps ? 'HTTPS' : 'HTTP Only'}</span>
            </div>
          </div>

          <div className="stat-card">
            <div className="stat-icon">🔗</div>
            <div className="stat-content">
              <span className="stat-label">Total Links</span>
              <span className="stat-value">{data.totalLinks}</span>
            </div>
          </div>

          <div className="stat-card">
            <div className="stat-icon">📊</div>
            <div className="stat-content">
              <span className="stat-label">HTTP Status</span>
              <span className="stat-value">{data.httpStatus}</span>
            </div>
          </div>
        </div>

        {/* Main Content Grid */}
        <div className="content-grid">
          {/* Left Column - Issues */}
          <div className="content-main">
            {/* Meta Details Section */}
            <div className="dashboard-section">
              <h3 className="section-title">📄 Meta Information</h3>
              <div className="meta-details">
                <div className="meta-item">
                  <span className="meta-label">Page Title:</span>
                  <span className="meta-value">{data.title || 'Not set'} ({data.titleLength} chars)</span>
                </div>
                <div className="meta-item">
                  <span className="meta-label">Meta Description:</span>
                  <span className="meta-value">
                    {data.metaDescription || 'Missing'} 
                    ({data.metaDescriptionLength} chars)
                  </span>
                </div>
                <div className="meta-item">
                  <span className="meta-label">H1 Tags:</span>
                  <span className="meta-value">{data.h1Tags ? data.h1Tags.length : 0}</span>
                </div>
                <div className="meta-item">
                  <span className="meta-label">H2 Tags:</span>
                  <span className="meta-value">{data.h2Tags ? data.h2Tags.length : 0}</span>
                </div>
              </div>
            </div>

            {/* Issues Section */}
            <div className="dashboard-section">
              <h3 className="section-title">🔍 Issues Detected ({data.issues ? data.issues.length : 0})</h3>
              
              {/* High Priority Issues */}
              {groupedIssues.HIGH && groupedIssues.HIGH.length > 0 && (
                <div className="issues-group">
                  <h4 className="issues-group-title high">High Priority ({groupedIssues.HIGH.length})</h4>
                  {groupedIssues.HIGH.map((issue, idx) => (
                    <div key={idx} className="issue-card high">
                      <div className="issue-header">
                        <span className="issue-code">{issue.ruleCode.replace(/_/g, ' ')}</span>
                        <span className={`severity-badge ${getSeverityClass(issue.severity)}`}>
                          {issue.severity}
                        </span>
                      </div>
                      <p className="issue-message">{issue.message}</p>
                      {data.recommendations && data.recommendations[issue.ruleCode] && (
                        <div className="issue-recommendation">
                          <strong>Fix:</strong> {data.recommendations[issue.ruleCode]}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {/* Medium Priority Issues */}
              {groupedIssues.MEDIUM && groupedIssues.MEDIUM.length > 0 && (
                <div className="issues-group">
                  <h4 className="issues-group-title medium">Medium Priority ({groupedIssues.MEDIUM.length})</h4>
                  {groupedIssues.MEDIUM.map((issue, idx) => (
                    <div key={idx} className="issue-card medium">
                      <div className="issue-header">
                        <span className="issue-code">{issue.ruleCode.replace(/_/g, ' ')}</span>
                        <span className={`severity-badge ${getSeverityClass(issue.severity)}`}>
                          {issue.severity}
                        </span>
                      </div>
                      <p className="issue-message">{issue.message}</p>
                      {data.recommendations && data.recommendations[issue.ruleCode] && (
                        <div className="issue-recommendation">
                          <strong>Fix:</strong> {data.recommendations[issue.ruleCode]}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {/* Low Priority Issues */}
              {groupedIssues.LOW && groupedIssues.LOW.length > 0 && (
                <div className="issues-group">
                  <h4 className="issues-group-title low">Low Priority ({groupedIssues.LOW.length})</h4>
                  {groupedIssues.LOW.map((issue, idx) => (
                    <div key={idx} className="issue-card low">
                      <div className="issue-header">
                        <span className="issue-code">{issue.ruleCode.replace(/_/g, ' ')}</span>
                        <span className={`severity-badge ${getSeverityClass(issue.severity)}`}>
                          {issue.severity}
                        </span>
                      </div>
                      <p className="issue-message">{issue.message}</p>
                      {data.recommendations && data.recommendations[issue.ruleCode] && (
                        <div className="issue-recommendation">
                          <strong>Fix:</strong> {data.recommendations[issue.ruleCode]}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {/* No Issues Found */}
              {(!data.issues || data.issues.length === 0) && (
                <div className="no-issues">
                  <p>✅ No critical issues found! Your website looks great.</p>
                </div>
              )}
            </div>
          </div>

          {/* Right Column - Technical Details */}
          <div className="content-sidebar">
            {/* Technical Details */}
            <div className="dashboard-section sidebar-section">
              <h3 className="section-title">⚙️ Technical Details</h3>
              <div className="technical-list">
                <div className="tech-item">
                  <span className="tech-label">Canonical Tag</span>
                  <span className="tech-value">{data.hasCanonical ? '✓ Yes' : '✗ No'}</span>
                </div>
                <div className="tech-item">
                  <span className="tech-label">Structured Data</span>
                  <span className="tech-value">{data.hasJsonLd ? '✓ Yes' : '✗ No'}</span>
                </div>
                <div className="tech-item">
                  <span className="tech-label">Sitemap Found</span>
                  <span className="tech-value">{data.sitemapFound ? '✓ Yes' : '✗ No'}</span>
                </div>
                <div className="tech-item">
                  <span className="tech-label">Robots.txt</span>
                  <span className="tech-value">{data.robotsTxtAccessible ? '✓ Accessible' : '✗ Not Found'}</span>
                </div>
                <div className="tech-item">
                  <span className="tech-label">Images Missing Alt</span>
                  <span className="tech-value">{data.imagesMissingAlt || 0}</span>
                </div>
              </div>
            </div>

            {/* Links Summary */}
            <div className="dashboard-section sidebar-section">
              <h3 className="section-title">🔗 Links Summary</h3>
              <div className="links-summary">
                <div className="link-stat">
                  <span className="link-number">{data.totalLinks}</span>
                  <span className="link-label">Total Links</span>
                </div>
                <div className="link-stat">
                  <span className="link-number">{data.internalLinks}</span>
                  <span className="link-label">Internal</span>
                </div>
                <div className="link-stat">
                  <span className="link-number">{data.externalLinks}</span>
                  <span className="link-label">External</span>
                </div>
              </div>
            </div>

            {/* Quick Actions */}
            <div className="dashboard-section sidebar-section quick-actions">
              <h3 className="section-title">⚡ Priority Actions</h3>
              <ul className="action-list">
                {!data.usesHttps && (
                  <li>🔒 Enable HTTPS with SSL certificate</li>
                )}
                {data.metaDescriptionLength === 0 && (
                  <li>📝 Add meta description</li>
                )}
                {!data.hasCanonical && (
                  <li>🔗 Add canonical tag</li>
                )}
                {data.imagesMissingAlt > 0 && (
                  <li>🖼️ Add alt text to images</li>
                )}
                {data.h1Tags && data.h1Tags.length > 1 && (
                  <li>📑 Use single H1 tag per page</li>
                )}
              </ul>
            </div>
          </div>
        </div>
      </div>

      <MainFooter />
    </>
  );
};

export default SeoResultDashboard;